import { Button, Classes, Dialog, H4 } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import BookContentWizardPanel from './book/contentWizardPanel';
import EventsContentWizardPanel from './events/contentWizardPanel';
import SimplePanelStack from './components/simplePanelStack';
import RecordSelectPanel from './book/record/recordSelectPanel';

const StyledDialog = styled(Dialog)`
  max-width: 1300px;
  width: 95%;
  margin: 20px 2.5%;
  padding-bottom: 0;
  background: #fff;
  align-self: flex-start;
`;

const StyledH4 = styled(H4)`
  flex: none !important;
`;

const getTitle = type => {
  switch (type) {
    case 'events':
      return 'Add Events';
    case 'records':
      return 'Choose Record(s)';
    default:
      return 'Add to Book';
  }
};

const getInitialPanel = type => {
  switch (type) {
    case 'events':
      return EventsContentWizardPanel;
    case 'records':
      return RecordSelectPanel;
    default:
      return BookContentWizardPanel;
  }
};

// TODO 264 Add tooltips to add buttons explaining why they are disabled? (everywhere)
// - Maybe, low priority.
const ContentWizardDialog = ({ params, close, isOpen, type }) => {
  const initialPanel = {
    props: { closeDialog: close, ...params },
    renderPanel: getInitialPanel(type),
    title: getTitle(type),
  };

  const [isMounted, setIsMounted] = useState(isOpen);
  const [panelStack, setPanelStack] = useState([initialPanel]);

  const currentPanel = useMemo(() => panelStack[panelStack.length - 1], [panelStack]);

  const previousPanel = useMemo(() => panelStack[panelStack.length - 2], [panelStack]);

  const addToPanelStack = useCallback(newPanel => setPanelStack(stack => [...stack, newPanel]), []);

  const removeFromPanelStack = useCallback(() => setPanelStack(stack => stack.slice(0, -1)), []);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  const handleClosed = () => {
    setIsMounted(false);
    setPanelStack([initialPanel]);
  };

  if (!isMounted) {
    return null;
  }

  const title = (
    <div className={Classes.PANEL_STACK2_HEADER}>
      <span>
        {previousPanel && (
          <Button
            className={Classes.PANEL_STACK2_HEADER_BACK}
            icon="chevron-left"
            minimal
            onClick={removeFromPanelStack}
            small
          >
            {previousPanel.title}
          </Button>
        )}
      </span>
      <StyledH4>{currentPanel.title}</StyledH4>
      <span />
    </div>
  );

  return (
    <StyledDialog
      isOpen={isOpen}
      onClose={close}
      onClosed={handleClosed}
      title={title}
      canOutsideClickClose={false}
    >
      <SimplePanelStack
        onClose={removeFromPanelStack}
        onOpen={addToPanelStack}
        // TODO 264 Render all panels? renderActivePanelOnly={false}
        showPanelHeader={false}
        stack={panelStack}
      />
    </StyledDialog>
  );
};

ContentWizardDialog.propTypes = {
  close: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['book', 'events', 'records']),
};

ContentWizardDialog.defaultProps = {
  type: 'book',
};

export default ContentWizardDialog;
